import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { renameFileForUpload } from "./safeUploadFileName";

const POPUP_IMAGE_MAX_MB = 5;
const POPUP_IMAGE_EXT = ["jpg", "jpeg", "png", "gif"];
const POPUP_IMAGE_MIME = ["image/jpeg", "image/jpg", "image/png", "image/gif"];

export const POPUP_IMAGE_REQUIRED_WIDTH = 435;
export const POPUP_IMAGE_REQUIRED_HEIGHT = 313;
export const POPUP_IMAGE_DIMENSION_LABEL = `${POPUP_IMAGE_REQUIRED_WIDTH}x${POPUP_IMAGE_REQUIRED_HEIGHT}px`;

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image file."));
    };
    img.src = url;
  });
}

export async function validatePopupImageFile(file) {
  if (!file) return { ok: false, message: "Please choose an image file." };

  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!POPUP_IMAGE_EXT.includes(ext)) {
    return {
      ok: false,
      message: `Unsupported format for ${file.name}. Only JPG, PNG, or GIF images are allowed.`,
    };
  }

  const mime = (file.type || "").toLowerCase();
  if (mime && !POPUP_IMAGE_MIME.includes(mime)) {
    return {
      ok: false,
      message: `Unsupported format for ${file.name}. Only JPG, PNG, or GIF images are allowed.`,
    };
  }

  if (file.size > POPUP_IMAGE_MAX_MB * 1024 * 1024) {
    return {
      ok: false,
      message: `${file.name} exceeds ${POPUP_IMAGE_MAX_MB}MB.`,
    };
  }

  try {
    const { width, height } = await getImageDimensions(file);
    if (width !== POPUP_IMAGE_REQUIRED_WIDTH || height !== POPUP_IMAGE_REQUIRED_HEIGHT) {
      return {
        ok: false,
        message: `The dimension should be ${POPUP_IMAGE_DIMENSION_LABEL}. Your image is ${width}x${height}px.`,
      };
    }
  } catch {
    return {
      ok: false,
      message: "Failed to validate image dimensions. Please choose a valid image file.",
    };
  }

  return { ok: true };
}

/**
 * Uploads directly under `fieldKey` (e.g. "puzzle_image"), which the backend
 * persists as-is on the theme record — no separate save step needed.
 */
export async function uploadPopupImage({ file, adminToken, currentTheme, fieldKey }) {
  const check = await validatePopupImageFile(file);
  if (!check.ok) throw new Error(check.message);

  const renamed = renameFileForUpload(file, "popup");
  const formData = new FormData();
  formData.append("themeName", currentTheme);
  formData.append(fieldKey, renamed);

  const data = await uploadThemeMedia({ formData, token: adminToken });
  const filename = data?.uploaded_files?.[fieldKey];
  if (!filename) {
    throw new Error("Image upload failed — no filename returned from server.");
  }
  return filename;
}
